<template lang="pug">
    .system-tabs
        tabs(:items="tabs" :active-tab="activeTab" @click="activateTab")
            //- Forwarded so a page can put its save buttons in the tab bar, the
            //- way every other form in the admin does.
            template(slot="tabbar-buttons")
                slot(name="tabbar-buttons")
        slot
</template>

<i18n>
{
    "de": {
        "Accounts": "Accounts",
        "Websites": "Webauftritte",
        "Billing": "Abrechnung",
        "Team": "Team",
        "Provider costs": "Drittanbieterkosten",
        "Platform": "Plattform"
    }
}
</i18n>

<script>
import Tabs from '@/components/Tabs';
import { useAuthStore, useSetupStore, useTicketStore } from '@/stores';

export default {
    name: 'SystemTabs',
    components: {
        Tabs,
    },
    props: {
        // A detail page lives below its section (/system/tickets/8) and still
        // belongs to that tab, so it says which one to mark.
        section: { type: String, default: null },
    },
    created() {
        // The badge has to be right from any /system page, not just the ticket
        // list — this component is on all of them.
        useTicketStore().subscribePlatformRealtime();
    },
    computed: {
        activeTab() {
            return this.section || this.$route.fullPath;
        },
        /**
         * A tab that only ever leads to a 403 is worse than no tab. Each screen
         * behind these enforces its own right; this just stops offering a door
         * somebody cannot open.
         */
        tabs() {
            const held = useAuthStore().platformRightKeys;

            return this.allTabs.filter((tab) => !tab.right || held.indexOf(tab.right) !== -1);
        },
        allTabs() {
            return [
                {
                    name: this.$t('Accounts'),
                    path: '/system/accounts',
                    right: 'platform:accounts',
                    icon: {
                        name: 'user-tie',
                        group: 'fas',
                    },
                },
                {
                    name: this.$t('Websites'),
                    path: '/system/abonements',
                    right: 'platform:accounts',
                    icon: {
                        name: 'cubes',
                        group: 'fas',
                    },
                },
                {
                    name: this.$t('Billing'),
                    path: '/system/billing',
                    right: 'platform:billing',
                    icon: {
                        name: 'file-invoice-dollar',
                        group: 'fas',
                    },
                },
                {
                    name: this.$t('Tickets'),
                    path: '/system/tickets',
                    icon: {
                        name: 'ticket',
                        group: 'fas',
                    },
                    // Same number as the sidebar: tickets with something
                    // nobody has read. Differing counts across levels is what
                    // makes people stop trusting badges.
                    badge: this.unreadTickets > 0 ? this.unreadTickets : undefined,
                },
                {
                    name: this.$t('Team'),
                    path: '/system/team',
                    icon: {
                        name: 'users',
                        group: 'fas',
                    },
                    right: 'platform:support-time',
                },
                {
                    name: this.$t('Provider costs'),
                    path: '/system/provider-costs',
                    right: 'platform:provider-costs',
                    icon: {
                        name: 'hand-holding-usd',
                        group: 'fas',
                    },
                },
                {
                    name: this.$t('Platform'),
                    path: '/system/settings',
                    right: 'platform:system',
                    icon: {
                        name: 'sliders',
                        group: 'fas',
                    },
                },
            ];
        },
        setupStore() {
            return useSetupStore();
        },
        unreadTickets() {
            return useTicketStore().platformUnreadCount;
        },
    },
    methods: {
        activateTab(item) {
            return this.setupStore.activateTab(item);
        },
    },
};
</script>
